/* Billingo — számlázó-program fiókok az asset-regiszterben (Beállítások → Számlázó
 * programok). M7, 2026-09-05 — wiki/proposals/asset-registry.md.
 *
 * A fiókonkénti kulcs-kezelés a generikus `AssetCredentialsPanel`-en megy (2026-09-05
 * óta: maszkolt érték-kijelzéssel, Gábor szabálya szerint). Itt marad a Billingo-
 * specifikus rész: a fiók-felvételi Modal (üres kulcs = a régi, asset nélküli készlet
 * átvétele) és a step-up indoklása.
 *
 * ⚠ Ez az ELSŐ termék-felhasználói Billingo credential-ÍRÓ felület (eddig csak a
 * seed-szkript írt). Ezért kezdettől step-up-os (2026-09-24 óta friss, erre a kérésre kötött
 * passkey: `requireFreshStepUp` + `freshRequest`/`sendFresh`): a mentés ELŐTT
 * (nem a 403-ra reagálunk — `.claude/rules/munkamenet-auth.md` 8.). A felület
 * kimondja, MIÉRT: a Billingo-válaszból jön a kimenő számlákon szereplő saját
 * bankszámlaszám, amit egy eltérített munkamenettel átírt kulcs átirányíthatna.
 *
 * ⚠ Több fiók: a backend `billingo_{asset_id}` készletet használ fiókonként, tehát
 * egy második fiók mentése NEM írja le az elsőt (2026-09-05 előtt igen).
 * A státusz-Badge SOHA nem vált „Aktív"-ra a mentéstől — csak a „Kapcsolat
 * tesztelése" ELMENTETT eredményétől (AssetStatusBlock). */
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus } from 'lucide-react';
import { freshRequest, sendFresh } from '../services/api';
import AssetStatusBlock from './AssetStatusBlock';
import AssetCredentialsPanel from './AssetCredentials';
import { Alert, Btn, Input, Modal } from './UI';
import { useToast } from './Toast';
import { useAuth } from '../contexts/AuthContext';
import { useStepUp } from '../contexts/StepUpContext';

const STEP_UP_WHY = 'A Billingo-kapcsolat módosítása előtt ';
const WHY_TEXT = 'A Billingo-kapcsolat adja meg a kimenő számláidon szereplő bankszámlaszámot — '
               + 'a módosítás előtt friss bejelentkezést kérünk.';

/* sessionStorage-piszkozat a felvételi Modalhoz. */
function useDraft(key) {
  const [draft, setDraft] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(key) || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    const any = Object.values(draft).some((v) => (v || '').trim() !== '');
    if (any) sessionStorage.setItem(key, JSON.stringify(draft));
    else sessionStorage.removeItem(key);
  }, [key, draft]);
  const clear = useCallback(() => { setDraft({}); sessionStorage.removeItem(key); }, [key]);
  return [draft, setDraft, clear];
}

/** Új Billingo-fiók felvétele — Modal, step-up a mentés előtt. */
function AddBillingoModal({ open, onClose }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { requireFreshStepUp } = useStepUp();
  const [draft, setDraft, clearDraft] = useDraft('billingo-cred-draft:new');
  const label = draft.label || '';
  const apiKey = draft.api_key || '';

  const createMut = useMutation({
    // 2026-09-24: a `freshRequest` leíró megy — ugyanaz, amire a passkey szól.
    mutationFn: (req) => sendFresh(req),
    onSuccess: (a) => {
      clearDraft();
      qc.invalidateQueries({ queryKey: ['company-assets', 'invoicing'] });
      toast.success(a.adopted_legacy
        ? 'Fiók felvéve — a korábban, rendszergazda által beállított kulcsot átvettük. Futtass élő tesztet.'
        : 'Fiók felvéve — a kapcsolat élő tesztig „Teszt szükséges".');
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A mentés nem sikerült.'),
  });

  const onSave = async () => {
    const credentials = apiKey.trim() ? { api_key: apiKey.trim() } : {};
    const req = freshRequest('POST /company-assets', { body: {
      asset_type: 'invoicing', provider: 'billingo', label: label.trim() || null, credentials } });
    if (!await requireFreshStepUp(STEP_UP_WHY, [req])) return;
    createMut.mutate(req);
  };

  return (
    <Modal open={open} onClose={onClose} title="Billingo-fiók hozzáadása" width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label="Fiók neve (opcionális)" value={label} autoComplete="off"
               placeholder="pl. Fő számlázó · Webshop"
               onChange={(e) => setDraft({ ...draft, label: e.target.value })}/>
        <Input label="Billingo API kulcs" type="password" value={apiKey} autoComplete="off" spellCheck={false}
               placeholder="Billingo → Beállítások → API kulcsok"
               onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}/>
        <Alert type="info">
          {WHY_TEXT}
          {' '}Ha a kulcsot üresen hagyod és ez az első fiók, a korábban rendszergazda által
          beállított kulcsot vesszük át. A fiók élő tesztig „Teszt szükséges" marad.
        </Alert>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} disabled={createMut.isPending}>Mégse</Btn>
          <Btn icon={KeyRound} onClick={onSave} loading={createMut.isPending}>Mentés</Btn>
        </div>
      </div>
    </Modal>
  );
}

export default function BillingoAccountsPanel() {
  const { isAdmin } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  return (
    <>
      <AssetStatusBlock assetType="invoicing" title="Számlázó programok"
        subtitle="Kimenő számlák szinkronja számlázó programból (ma: Billingo). Fiókonként külön kulcs — egy második fiók nem írja le az elsőt."
        emptyText={'Még nincs bekötött számlázó program. A „Billingo-fiók hozzáadása” gombbal veheted fel az elsőt.'}
        actions={(
          <Btn size="sm" icon={Plus} disabled={!isAdmin}
               title={isAdmin ? undefined : 'Csak cég-adminisztrátor módosíthatja.'}
               onClick={() => setAddOpen(true)}>Billingo-fiók hozzáadása</Btn>
        )}
        renderExtra={(a) => (a.provider === 'billingo'
          ? <AssetCredentialsPanel asset={a} stepUpWhy={STEP_UP_WHY} whyText={WHY_TEXT}/>
          : null)}/>
      <AddBillingoModal open={addOpen} onClose={() => setAddOpen(false)}/>
    </>
  );
}
